import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import AhamLogo from '../../img/Aham_logo_1.png';
import {
  PhoneIcon,
  MapPinIcon,
  StarIcon,
  MagnifyingGlassIcon,
  UserIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

import { getPhoneLink, scrollToSection } from '../../utils/helpers';
import { useNavigate, Link } from "react-router-dom";
import { FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn, FaYoutube } from "react-icons/fa";

const Header = ({ currentLanguage, onLanguageChange, onCreditScoreClick }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [expandedMobileItem, setExpandedMobileItem] = useState(null); // ✅ Added for mobile submenus
  const [activeLoanKey, setActiveLoanKey] = useState("construction");

  // ✅ Font Size Control (A-, A, A+)
  const [fontSize, setFontSize] = useState(() => {
    const saved = localStorage.getItem('aham-font-size');
    return saved ? parseInt(saved, 10) : 0;
  });

  // ✅ Language Dropdown Control
  const [showLang, setShowLang] = useState(false); // ✅ Added

  // ✅ Logo Tooltip Control
  const [showLogoTooltip, setShowLogoTooltip] = useState(false);

  // ✅ Stable hover delay (fix flicker issue)
  const openDropdown = (key) => {
    clearTimeout(window.__dropdownTimer);
    window.__dropdownTimer = setTimeout(() => setActiveDropdown(key), 120);
  };
  const closeDropdown = () => {
    clearTimeout(window.__dropdownTimer);
    window.__dropdownTimer = setTimeout(() => setActiveDropdown(null), 200);
  };

  const handleNavigation = (href) => {
    if (href.startsWith('#')) {
      const sectionId = href.substring(1);
      if (window.location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          scrollToSection(sectionId);
        }, 600); // Wait for homepage load/animate
      } else {
        // Delay for same-page scroll to allow mobile menu to close first
        setTimeout(() => {
          scrollToSection(sectionId);
        }, 350);
      }
    } else {
      navigate(href);
    }
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);






  // ✅ Apply font size to document
  useEffect(() => {
    const scale = 100 + (fontSize * 10); // -3 = 70%, 0 = 100%, +3 = 130%
    document.documentElement.style.fontSize = `${scale}%`;
    localStorage.setItem('aham-font-size', fontSize.toString());
  }, [fontSize]);

  // ✅ Font size handlers
  const decreaseFontSize = () => setFontSize(prev => Math.max(-3, prev - 1));
  const resetFontSize = () => setFontSize(0);
  const increaseFontSize = () => setFontSize(prev => Math.min(3, prev + 1));





  // ✅ FULL MENU LIST (ALL MENUS RESTORED + Home Loans Mega Menu)
  const navigationItems = [
    {
      key: 'loans',
      label: 'Home Loans',
      //megaMenu:true,
      hasDropdown: true,
      submenu: [
        { key: 'construction', label: 'Home Construction Loan', href: '/home-construction-loan-india', description: 'Get the funds you need to build your dream home, released in easy stages as your construction grows.', image: '/img/megamenu/HomeConstructionLoan.jpg' },
        { key: 'purchase', label: 'Home Purchase or Resale Loan', href: '/home-purchase-loan', description: 'Finance to purchase a new or resale home, with flexible repayment options that suit your budget.', image: '/img/megamenu/HomeConstructionLoan.jpg' },
        { key: 'composite', label: 'Composite Loan ', href: '/composite-home-loan-plot-construction', description: 'Buy your plot and build your home on it with a single, convenient loan structured in two stages.', image: '/img/megamenu/HomeConstructionLoan.jpg' },
        { key: 'renovation', label: 'Home Renovation Loan', href: '/home-renovation-loan', description: 'Upgrade your existing home with funds for remodeling, interiors, repairs, and modern enhancements.', image: '/img/megamenu/HomeConstructionLoan.jpg' },
        { key: 'extension', label: 'Home Extension Loan', href: '/home-extension-loan', description: 'Add more space to your home to fit your lifestyle, whether it’s a new room, floor, or extended area.', image: '/img/megamenu/HomeConstructionLoan.jpg' },
        { key: 'balance', label: 'Balance Transfer Loan', href: '/home-loan-balance-transfer', description: 'Move your existing home loan to us and reduce your EMIs with better interest rates and smoother repayment.', image: '/img/megamenu/HomeConstructionLoan.jpg' },
        { key: 'nri', label: 'NRI Home Loan', href: '/nri-home-loan-india', description: 'Tailored financing solutions for NRIs to purchase, build, or improve a home in India with simplified documentation.', image: '/img/megamenu/HomeConstructionLoan.jpg' },
        { key: 'topup', label: 'Top-up Loan', href: '/top-up-loan-for-home-loan', description: 'Get additional funds on your existing loan for personal or home needs without extra paperwork.', image: '/img/megamenu/HomeConstructionLoan.jpg' },
        { key: 'mortgageLoans', label: 'Mortgage Loans', href: '/loan-against-property-mortgage-loan', description: 'Get additional funds on your existing loan for personal or home needs without extra paperwork.', image: '/img/megamenu/HomeConstructionLoan.jpg' }

      ]
    },

    // { key: 'calculator', label: 'Calculator', href: '/#' },
    {
      key: 'calculator',
      label: 'Calculators',
      hasDropdown: true,
      submenu: [
       { label: 'Scorecard', href: '/home-loan-eligibility-calculator' },
         { label: 'EMI Calculator', href: '/home-loan-emi-calculator' },
       // { label: 'Scorecard', href: '#scorecard' },
       // { label: 'EMI Calculator', href: '#emi-calculator' },
      ]
    },


    { key: 'pmay', label: 'PMAY 2.0', href: '/pmay-2-0-scheme-apply-online' },

    {
      key: 'partnerUs',
      label: 'Partner Us',
      hasDropdown: true,
      submenu: [
        { label: 'Partner Us', href: '/become-home-loan-dsa-partner' },
        { label: 'DSA Onboarding', href: '/home-loan-dsa-registration-online' },
      ]
    },

    {
      key: 'Governance',
      label: 'Governance',
      hasDropdown: true,
      submenu: [
        { key: 'governance', label: 'Governance', href: '/corporate-governance-policies' },
        { key: 'grievances', label: 'Grievances', href: '/grievance-redressal-policy-customer-support' },
        { key: 'risk-classification', label: 'Risk Classification', href: '/risk-classification-framework-policy' },
        { key: 'ratings', label: 'Ratings Certificate', href: '/aham-housing-finance-credit-rating-certificate' },
      ],
    },

    {
      key: 'news',
      label: 'News Room',
      hasDropdown: true,
      submenu: [
        { key: 'e-auction', label: 'Property for Sale', href: '/aham-housing-finance-e-auction-sale-notice' },

        { key: 'information', label: 'Secured Asset', href: '/secured-assets-sarfaesi-aham-housing-finance' },
        { key: 'new', label: 'What’s New in AHAM', href: '/home-loan-blog-aham-housing-finance' },
      ],
    },

    {
      key: 'careers',
      label: 'Careers',
      hasDropdown: true,
      submenu: [
        { key: 'careers', label: 'Life @ Aham', href: '/life-at-aham-housing-finance-careers' },
        { key: 'gallery', label: 'Gallery', href: '/aham-moments-home-loan-success-stories' },

        { key: 'openings', label: 'Current Openings', href: '/career' },
      ],
    },

    {
      key: 'about',
      label: t('header.navigation.aboutUs'),
      hasDropdown: true,
      submenu: [
        { key: 'aboutUs', label: 'About Aham', href: '/about-aham-housing-finance' },
        { key: 'managementTeam', label: 'Our Team', href: '/aham-housing-finance-partnership-channel-partner' },
        { key: 'investorinfo', label: 'Investor Info', href: '/investor-information' },
      ],
    },
  ];

  const socialLinks = [
    { name: 'Facebook', href: 'https://www.facebook.com/ahamhfc', icon: <FaFacebookF /> },
    //{ name: 'Twitter', href: 'https://twitter.com', icon: <FaTwitter /> },
    { name: 'Instagram', href: 'https://www.instagram.com/aham_housingfinance/', icon: <FaInstagram /> },
    { name: 'LinkedIn', href: 'https://linkedin.com/company/ahamhfc?originalSubdomain=in', icon: <FaLinkedinIn /> },
    { name: 'YouTube', href: 'https://www.youtube.com/@ahamhfc', icon: <FaYoutube /> }
  ];


  return (
    <>
      {/* TOP BAR (unchanged) */}
      {/* <div className="bg-primary-600 text-white py-2 text-sm flex justify-center gap-6">
        <a href={getPhoneLink()} className="flex items-center gap-2"><PhoneIcon className="h-4" />{t('header.call')}</a>
        <span>|</span>
        <button onClick={() => navigate('/contact')} className="flex items-center gap-2 bg-yellow-500 text-primary-900 px-3 py-1 rounded-full animate-pulse"><MapPinIcon className="h-4" />{t('header.branchLocator')}</button>
        <span>|</span>
         <button onClick={() => navigate('/blog')} className="flex items-center gap-2  ">{t('header.blog')}</button>
        <span>|</span>
        
        
        <button onClick={onCreditScoreClick} className="flex items-center gap-2 bg-yellow-500 text-primary-900 px-3 py-1 rounded-full animate-pulse"><StarIcon className="h-4" />{t('header.creditScore')}</button>
      
      </div> */}

           <div className="bg-primary-600 text-white py-2 text-sm sticky top-0 z-[10001] md:relative md:top-auto">
        <div className="container mx-auto px-4 flex items-center">


          {/* DESKTOP TOP BAR (Visible on md and above) */}
          <div className="hidden md:flex flex-1 justify-center items-center gap-6">
            {/* Font Size Controls */}
            <div className="flex items-center gap-1 border-r border-white/30 pr-4">
              <button
                onClick={decreaseFontSize}
                disabled={fontSize <= -3}
                className={`px-2 py-1 text-sm font-bold rounded transition ${fontSize <= -3
                  ? 'text-white/40 cursor-not-allowed'
                  : 'text-white hover:bg-white/20'
                  }`}
                title="Decrease text size"
              >
                A-
              </button>
              <button
                onClick={resetFontSize}
                className={`px-2 py-1 text-sm font-bold rounded transition ${fontSize === 0
                  ? 'bg-white/20 text-yellow-300'
                  : 'text-white hover:bg-white/20'
                  }`}
                title="Reset text size"
              >
                A
              </button>
              <button
                onClick={increaseFontSize}
                disabled={fontSize >= 3}
                className={`px-2 py-1 text-sm font-bold rounded transition ${fontSize >= 3
                  ? 'text-white/40 cursor-not-allowed'
                  : 'text-white hover:bg-white/20'
                  }`}
                title="Increase text size"
              >
                A+
              </button>
            </div>

            <button
              onClick={() => {
                if (window.location.pathname !== "/") {
                  navigate("/");
                  setTimeout(() => {
                    scrollToSection("important-news");
                  }, 600);
                } else {
                  setTimeout(() => {
                    scrollToSection("important-news");
                  }, 350);
                }
              }}
              className="flex items-center gap-2 bg-yellow-500 text-primary-900 px-3 py-1 rounded-full animate-pulse"
            >
              <StarIcon className="h-4" /> {t('header.annoncement')}
            </button>

            <span>|</span>

            <a href={getPhoneLink()} className="flex items-center gap-2">
              <motion.div
                animate={{
                  rotate: [0, -15, 15, -15, 15, -10, 10, -5, 5, 0],
                }}
                transition={{
                  duration: 0.8,
                  repeat: Infinity,
                  repeatDelay: 1.5,
                  ease: "easeInOut"
                }}
              >
                <PhoneIcon className="h-4 text-red-600" />
              </motion.div> {t('header.call')}
            </a>

            <span>|</span>

            <button onClick={() => navigate('/branch-locator-aham-housing-finance-home-loan')} className="flex items-center gap-2 ">
              <MapPinIcon className="h-4" /> {t('header.branchLocator')}
            </button>

            <span>|</span>

            <button onClick={() => navigate('/home-loan-blog-aham-housing-finance')} className="flex items-center gap-2">
              {t('header.blog')}
            </button>

            <span>|</span>

            <button onClick={onCreditScoreClick} className="flex items-center gap-2 bg-yellow-500 text-primary-900 px-3 py-1 rounded-full animate-pulse">
              <StarIcon className="h-4" /> {t('header.creditScore')}
            </button>
          </div>

          {/* MOBILE TOP BAR (Visible on mobile only) */}
          <div className="flex md:hidden flex-1 justify-between items-center px-1">
            <button
              onClick={() => navigate('/branch-locator-aham-housing-finance-home-loan')}
              className="flex items-center gap-1.5 text-[11px] font-semibold"
            >
              <MapPinIcon className="h-3.5 w-3.5" />
              Branches
            </button>

            <button
              onClick={onCreditScoreClick}
              className="flex items-center gap-1.5 bg-yellow-500 text-primary-900 px-2.5 py-1 rounded-full text-[10px] font-bold animate-pulse"
            >
              <StarIcon className="h-3.5 w-3.5 text-red-600" />
              Free Credit Score
            </button>

            <a
              href={getPhoneLink()}
              className="flex items-center gap-1.5 text-[11px] font-semibold"
            >
              <motion.div
                animate={{ rotate: [0, -15, 15, -15, 15, 0] }}
                transition={{ duration: 0.8, repeat: Infinity, repeatDelay: 1.5 }}
              >
                <PhoneIcon className="h-3.5 w-3.5 text-red-600" />
              </motion.div>
              Call
            </a>
          </div>

          {/* SOCIAL ICONS - Desktop Only */}
          <div className="hidden md:flex justify-end items-center gap-4">
            {/* Font Size Controls */}

            {/* <div className="flex items-center gap-1 border-r border-white/30 pr-4">
              <button
                onClick={decreaseFontSize}
                disabled={fontSize <= -3}
                className={`px-2 py-1 text-sm font-bold rounded transition ${fontSize <= -3
                  ? 'text-white/40 cursor-not-allowed'
                  : 'text-white hover:bg-white/20'
                  }`}
                title="Decrease text size"
              >
                A-
              </button>
              <button
                onClick={resetFontSize}
                className={`px-2 py-1 text-sm font-bold rounded transition ${fontSize === 0
                  ? 'bg-white/20 text-yellow-300'
                  : 'text-white hover:bg-white/20'
                  }`}
                title="Reset text size"
              >
                A
              </button>
              <button
                onClick={increaseFontSize}
                disabled={fontSize >= 3}
                className={`px-2 py-1 text-sm font-bold rounded transition ${fontSize >= 3
                  ? 'text-white/40 cursor-not-allowed'
                  : 'text-white hover:bg-white/20'
                  }`}
                title="Increase text size"
              >
                A+
              </button>
            </div> */}
            {/* Font Size Controls */}
            {/* Social Icons */}
            {socialLinks.map((social, index) => (
              <a
                key={index}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-yellow-300 text-lg transition"
              >
                {social.icon}
              </a>
            ))}
          </div>

        </div>
      </div>



      {/* MAIN HEADER */}
          <header className={`sticky top-[44px] md:top-0 z-[10000] ${isScrolled ? 'bg-white/90 backdrop-blur shadow' : 'bg-white'}`}>

        <div className="container mx-auto px-4 flex justify-between items-center h-20">

          <div
            className="relative flex items-center"
            onMouseEnter={() => setShowLogoTooltip(true)}
            onMouseLeave={() => setShowLogoTooltip(false)}
          >
            <Link to="/">
              <img
                src={AhamLogo}
                className="w-32 transition-transform duration-300 hover:scale-105"
                alt="AHAM Housing Finance Limited Logo"
              />
            </Link>

            <AnimatePresence>
              {showLogoTooltip && (
                <motion.div
                  initial={{ opacity: 0, y: 10, x: '-50%' }}
                  animate={{ opacity: 1, y: 0, x: '-50%' }}
                  exit={{ opacity: 0, y: 10, x: '-50%' }}
                  className="absolute left-1/2 -bottom-10 z-[10000] pointer-events-none"
                >
                  <div className="bg-[#ed2636] text-white text-[10px] font-bold px-4 py-1.5 rounded-full shadow-xl whitespace-nowrap uppercase tracking-widest flex items-center justify-center">
                    Home
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#ed2636] rotate-45" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* DESKTOP NAVIGATION */}
          <nav className="hidden lg:flex items-center gap-6">

            {navigationItems.map((item) => (
              <div key={item.key} className="relative"
                onMouseEnter={() => openDropdown(item.key)}
                onMouseLeave={() => closeDropdown()}
              >

                {/* ✅ HOME LOANS → MEGA MENU */}
                {item.megaMenu ? (
                  <>
                    <button className="flex items-center gap-1 hover:text-primary-600 font-medium">
                      {item.label}<ChevronDownIcon className={`h-4 transition ${activeDropdown === item.key ? 'rotate-180' : ''}`} />
                    </button>

                    {activeDropdown === item.key && (
                      <div className="absolute left-0 mt-3 w-[950px] bg-[#E8F3FF] rounded-xl shadow-lg p-6 flex gap-6 z-50">

                        {/* LEFT COLUMN */}
                        <div className="w-1/3 border-r pr-4">
                          {item.submenu.map((loan) => (
                            <div key={loan.key}
                              onMouseEnter={() => setActiveLoanKey(loan.key)}
                              className={`py-3 px-3 rounded-md text-sm cursor-pointer font-medium transition 
                              ${activeLoanKey === loan.key ? "bg-white text-primary-700 shadow" : "hover:bg-white hover:text-primary-700"}`}>
                              {loan.label}
                            </div>
                          ))}
                        </div>

                        {/* RIGHT COLUMN */}
                        <div className="w-2/3 flex gap-6 items-start">
                          <div className="w-2/3">
                            <h3 className="text-xl font-semibold text-primary-800 mb-2">
                              {item.submenu.find(l => l.key === activeLoanKey)?.label}
                            </h3>
                            <p className="text-gray-700 mb-4">
                              {item.submenu.find(l => l.key === activeLoanKey)?.description}
                            </p>

                            <div className="flex gap-3">
                              <Link to={item.submenu.find(l => l.key === activeLoanKey)?.href}>
                                <button className="bg-primary-600 text-white px-4 py-2 rounded-md">Explore More</button>
                              </Link>
                              <Link to="/contact">
                                <button className="bg-[#003B95] text-white px-4 py-2 rounded-md">Apply Now</button>
                              </Link>
                            </div>
                          </div>

                          <img src={item.submenu.find(l => l.key === activeLoanKey)?.image}
                            alt={`${item.submenu.find(l => l.key === activeLoanKey)?.label} - AHAM Housing Finance`}
                            className="w-40 h-40 rounded-lg object-cover shadow" />

                        </div>

                      </div>
                    )}
                  </>
                ) : (
                  /* ✅ NORMAL DROPDOWNS (UNCHANGED) */
                  <>
                    {item.hasDropdown ? (
                      <>
                        <button className="flex items-center gap-1 hover:text-red-600 font-medium text-sm">
                          {item.label}<ChevronDownIcon className="h-4" />
                        </button>

                        {activeDropdown === item.key && (
                          <div className="absolute left-0 mt-3 w-56 bg-white rounded-lg shadow-lg py-2 z-50">

                            {item.submenu.map((subItem, index) => (
                              <button key={index} onClick={() => handleNavigation(subItem.href)}
                                className="block w-full text-left hover:text-red-600 px-4 py-3 text-sm hover:bg-gray-50">
                                {subItem.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <button onClick={() => navigate(item.href)} className="flex items-center gap-1 hover:text-red-600 font-medium text-sm">
                        {item.label}
                      </button>
                    )}
                  </>
                )}

              </div>
            ))}

          </nav>



          {/* ✅ RIGHT SIDE (Add Search + Language) */}
          <div className="hidden md:flex items-center gap-6">

            {/* ✅ Search Icon */}
            {/* <button className="text-gray-700 hover:text-primary-600">
              <MagnifyingGlassIcon className="h-5 w-5" />
            </button> */}

            {/* ✅ Language Selector */}
            <div className="relative">
              <button onClick={() => setShowLang(!showLang)} className="flex items-center gap-1 text-gray-700 hover:text-primary-600">
                <GlobeAltIcon className="h-5 w-5" />
                <span>{currentLanguage === "ta" ? "தமிழ்" : "En"}</span>
                <ChevronDownIcon className="h-4" />
              </button>

              <AnimatePresence>
                {showLang && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    className="absolute right-0 mt-2 bg-white shadow-lg rounded-md overflow-hidden z-50"
                  >
                    <button onClick={() => { onLanguageChange("en"); setShowLang(false); }} className="block px-4 py-2 text-left hover:bg-gray-100">
                      English
                    </button>
                    <button onClick={() => { onLanguageChange("ta"); setShowLang(false); }} className="block px-4 py-2 text-left hover:bg-gray-100">
                      தமிழ்
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* LOGIN BUTTON ✅ unchanged */}
            <Link to="/contact-aham-housing-finance">
              <button className="bg-[#ed2636] text-white px-4 py-2 rounded-lg hover:bg-primary-700">
                {t('header.customerLogin')}
              </button>
            </Link>
          </div>

          {/* MOBILE MENU ICON */}
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden p-2">
            {isMobileMenuOpen ? <XMarkIcon className="h-6" /> : <Bars3Icon className="h-6" />}
          </button>

        </div>

        {/* ✅ MOBILE MENU (WITH SUBMENU SUPPORT) */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="lg:hidden bg-white shadow-md overflow-hidden border-t"
            >
              <div className="px-4 py-4 space-y-1">
                {navigationItems.map((item, index) => (
                  <div key={index} className="border-b border-gray-50 last:border-none">
                    {item.hasDropdown ? (
                      <>
                        <button
                          onClick={() => setExpandedMobileItem(expandedMobileItem === item.key ? null : item.key)}
                          className="flex justify-between items-center w-full text-left py-3 font-medium text-gray-800"
                        >
                          {item.label}
                          <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${expandedMobileItem === item.key ? 'rotate-180' : ''}`} />
                        </button>
                        <AnimatePresence>
                          {expandedMobileItem === item.key && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden bg-gray-50/50 rounded-lg mb-2"
                            >
                              <div className="py-1 px-4 space-y-1">
                                {item.submenu.map((subItem, subIndex) => (
                                  <button
                                    key={subIndex}
                                    onClick={() => {
                                      setIsMobileMenuOpen(false);
                                      handleNavigation(subItem.href);
                                    }}
                                    className="block w-full text-left py-2.5 text-sm text-gray-600 hover:text-red-600 border-b border-gray-100 last:border-none"
                                  >
                                    {subItem.label}
                                  </button>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <button
                        onClick={() => { navigate(item.href); setIsMobileMenuOpen(false); }}
                        className="block w-full text-left py-3 font-medium text-gray-800"
                      >
                        {item.label}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      </header>
    </>
  );
};

export default Header;
